import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";

import { fetchAll, LOG_MAX_ROWS } from "@/lib/fetchAll";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/data-table/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { TruncationNotice } from "@/components/TruncationNotice";

export const Route = createFileRoute("/_app/audit-log/")({ component: AuditLogPage });

interface AuditRow {
  id: number;
  user_id: number | null;
  email: string | null;
  user_name: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  ip: string | null;
  ts: number;
  before_json: string | null;
  after_json: string | null;
}

function AuditLogPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["audit-log"],
    // Bounded, not exhaustive: the audit log only ever grows (4,300+ rows and
    // counting), and OFFSET paging rescans everything it skips — so "fetch all"
    // would get quadratically slower forever. The most recent window is what
    // this screen is actually for; TruncationNotice makes the limit visible.
    queryFn: () => fetchAll<AuditRow>("/admin/audit", { maxRows: LOG_MAX_ROWS }),
  });

  const columns: ColumnDef<AuditRow, unknown>[] = [
    {
      accessorKey: "ts", header: "When",
      cell: ({ row }) => <span className="font-mono text-xs">{format(row.original.ts * 1000, "MMM d, HH:mm:ss")}</span>,
    },
    {
      id: "user", header: "User",
      cell: ({ row }) => row.original.email
        ? <div><p className="text-sm">{row.original.user_name}</p><p className="text-xs text-muted-foreground">{row.original.email}</p></div>
        : <span className="text-muted-foreground text-xs">system</span>,
    },
    {
      accessorKey: "action", header: "Action",
      cell: ({ row }) => <Badge variant="outline">{row.original.action}</Badge>,
      filterFn: (r, _i, v) => r.getValue("action") === v,
    },
    {
      accessorKey: "resource_type", header: "Resource",
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.resource_type}
          {row.original.resource_id && <span className="ml-1 text-muted-foreground">#{row.original.resource_id}</span>}
        </span>
      ),
      filterFn: (r, _i, v) => r.getValue("resource_type") === v,
    },
    {
      accessorKey: "ip", header: "IP",
      cell: ({ row }) => row.original.ip ? <span className="font-mono text-xs">{row.original.ip}</span> : <span className="text-muted-foreground">—</span>,
    },
  ];

  // Distinct resource and action lists derived from data
  const distinctResources = Array.from(new Set((data?.items ?? []).map((r) => r.resource_type))).sort();
  const distinctActions   = Array.from(new Set((data?.items ?? []).map((r) => r.action))).sort();

  return (
    <div className="space-y-2">
      <PageHeader
        title="Audit log"
        description="Every mutation across the CMS, with who and from where. Retained for 1 year."
      />
      {data?.truncated && <TruncationNotice count={data.items.length} noun="events" />}
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        globalSearch
        globalSearchPlaceholder="Search by name, action, resource…"
        pageSize={50}
        filters={[
          { id: "resource_type", label: "Resource", options: distinctResources.map((r) => ({ value: r, label: r })) },
          { id: "action",        label: "Action",   options: distinctActions.map((a)   => ({ value: a, label: a })) },
        ]}
        emptyMessage={isLoading ? "Loading…" : "No events yet."}
      />
    </div>
  );
}
